import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Loader2,
  Eye,
  Building2,
  Users,
  Calendar,
  Hash,
  Receipt,
  CreditCard,
  AlertCircle,
  Sparkles,
  CircleDollarSign,
} from "lucide-react";
import { AxiosError } from "axios";
import { useIssuers, useNextInvoiceNumber } from "../api/issuers";
import { useClientes } from "../api/clientes";
import { useConceptos } from "../api/conceptos";
import { useFormasPago } from "../api/formas-pago";
import { useCreateFactura, previewFacturaPdf } from "../api/facturas";
import { useBillingConfig } from "@modules/settings/api/billing";
import type { FacturaFormData } from "../types/factura";

interface LineaLocal {
  concepto_id: number | null;
  concepto: string | null;
  descripcion: string;
  cantidad: number;
  precio_unitario: number;
  iva_porcentaje: number;
}
interface SuplidoLocal {
  descripcion: string;
  importe: number;
}

const EMPTY_LINEA: LineaLocal = {
  concepto_id: null,
  concepto: null,
  descripcion: "",
  cantidad: 1,
  precio_unitario: 0,
  iva_porcentaje: 21,
};

function formatEUR(n: number): string {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(n);
}

export function FacturaFormPage() {
  const navigate = useNavigate();
  const createFactura = useCreateFactura();

  // ── Cabecera
  const [emisorId, setEmisorId] = useState<number | null>(null);
  const [clienteId, setClienteId] = useState<number | null>(null);
  // Nº de factura: siempre lo autogenera el backend. Mantenemos el estado solo
  // para enviar undefined (no override) en el payload.
  const numero = "";
  const [fecha, setFecha] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [vencimiento, setVencimiento] = useState<string>("");
  const [numeroPedido, setNumeroPedido] = useState("");
  const [numeroOferta, setNumeroOferta] = useState("");

  // ── Líneas y suplidos
  const [lineas, setLineas] = useState<LineaLocal[]>([{ ...EMPTY_LINEA }]);
  const [suplidos, setSuplidos] = useState<SuplidoLocal[]>([]);

  // ── Forma de pago
  const [formaPagoId, setFormaPagoId] = useState<number | null>(null);

  // ── Errores
  const [serverError, setServerError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const { data: emisores } = useIssuers();
  const { data: clientes } = useClientes(emisorId ?? undefined);
  const { data: conceptos } = useConceptos(
    emisorId ?? undefined,
    clienteId ?? undefined,
  );
  const { data: formasPago } = useFormasPago(clienteId ?? undefined);
  const { data: nextNumber } = useNextInvoiceNumber(emisorId);
  const { data: billingCfg } = useBillingConfig();
  const ivaTipos = billingCfg?.iva_tipos ?? [21, 10, 4, 0];
  const ivaDefecto = billingCfg?.iva_defecto ?? 21;

  // Aplica el IVA por defecto configurado a la primera línea (aún vacía).
  useEffect(() => {
    if (billingCfg == null) return;
    setLineas((ls) => {
      if (ls.length !== 1) return ls;
      const l = ls[0];
      const pristina = !l.concepto_id && !l.descripcion && !l.precio_unitario;
      if (!pristina || l.iva_porcentaje === ivaDefecto) return ls;
      return [{ ...l, iva_porcentaje: ivaDefecto }];
    });
  }, [billingCfg, ivaDefecto]);

  // Si solo hay un emisor, autoselecciónalo
  useEffect(() => {
    if (!emisorId && emisores?.length === 1) {
      setEmisorId(emisores[0].id);
    }
  }, [emisores, emisorId]);

  // Si cambia cliente, resetea forma de pago a la predeterminada del cliente
  useEffect(() => {
    if (!formasPago || formasPago.length === 0) {
      setFormaPagoId(null);
      return;
    }
    const def = formasPago.find((fp) => fp.es_predeterminada) ?? formasPago[0];
    setFormaPagoId(def.id);
  }, [formasPago]);

  // Auto-calcular vencimiento cuando cambia forma de pago o fecha,
  // según el plazo_dias de la forma de pago seleccionada.
  // El usuario puede sobrescribir manualmente después.
  useEffect(() => {
    const fp = formasPago?.find((x) => x.id === formaPagoId);
    if (!fp || !fp.plazo_dias || !fecha) return;
    const d = new Date(fecha + "T00:00:00");
    d.setDate(d.getDate() + fp.plazo_dias);
    const newVenc = d.toISOString().slice(0, 10);
    setVencimiento(newVenc);
    // Solo cuando cambia la forma o la fecha. Si el usuario edita vencimiento
    // manualmente, su edición persiste hasta el próximo cambio de estas deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formaPagoId, fecha, formasPago]);

  // ── Totales calculados
  const totales = useMemo(() => {
    let sub = 0,
      iva = 0;
    for (const l of lineas) {
      const base = (l.cantidad || 0) * (l.precio_unitario || 0);
      sub += base;
      iva += (base * (l.iva_porcentaje || 0)) / 100;
    }
    const sup = suplidos.reduce((acc, s) => acc + (s.importe || 0), 0);
    return {
      subtotal: sub,
      iva,
      suplidos_total: sup,
      total: sub + iva + sup,
    };
  }, [lineas, suplidos]);

  function updateLinea(idx: number, patch: Partial<LineaLocal>) {
    setLineas((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }

  function aplicarConcepto(idx: number, conceptoId: number | null) {
    if (conceptoId === null) {
      updateLinea(idx, { concepto_id: null, concepto: null });
      return;
    }
    const c = conceptos?.find((x) => x.id === conceptoId);
    if (!c) return;
    // Cast explícito por si el backend devuelve los decimales como string
    const ivaNum = Number(c.iva);
    const precioNum =
      c.precio_unitario === null || c.precio_unitario === undefined
        ? 0
        : Number(c.precio_unitario);
    // El concepto aporta nombre, precio e IVA. La descripción queda libre: solo
    // se rellena si está vacía, así no se pisa lo que el usuario ya escribió y
    // puede editarla tras elegir el concepto. En el PDF se muestran ambos.
    const descripcionActual = (lineas[idx]?.descripcion ?? "").trim();
    updateLinea(idx, {
      concepto_id: c.id,
      concepto: c.descripcion,
      ...(descripcionActual === "" ? { descripcion: c.descripcion } : {}),
      precio_unitario: Number.isFinite(precioNum) ? precioNum : 0,
      // Fallback al IVA por defecto si el concepto legacy guardó iva=null o 0
      iva_porcentaje: Number.isFinite(ivaNum) && ivaNum > 0 ? ivaNum : ivaDefecto,
    });
  }

  function buildPayload(): FacturaFormData | null {
    if (!emisorId || !clienteId) return null;
    const formaPago = formasPago?.find((fp) => fp.id === formaPagoId);
    return {
      emisor_id: emisorId,
      cliente_id: clienteId,
      // En la previsualización mostramos el número que se autogenerará.
      numero: numero || nextNumber || undefined,
      fecha,
      vencimiento: vencimiento || null,
      numero_pedido: numeroPedido || null,
      numero_oferta: numeroOferta || null,
      forma_pago_id: formaPago?.id ?? null,
      forma_pago_etiqueta: formaPago?.etiqueta ?? null,
      forma_pago_plazo_dias: formaPago?.plazo_dias ?? null,
      forma_pago_iban: formaPago?.iban ?? null,
      forma_pago_notas: formaPago?.notas ?? null,
      lineas: lineas.map((l) => ({
        concepto: l.concepto || null,
        descripcion: l.descripcion,
        cantidad: Number(l.cantidad),
        precio_unitario: Number(l.precio_unitario),
        iva_porcentaje: Number(l.iva_porcentaje),
      })),
      suplidos: suplidos
        .filter((s) => s.descripcion || s.importe)
        .map((s) => ({
          descripcion: s.descripcion || null,
          importe: Number(s.importe),
        })),
    };
  }

  async function handlePreview() {
    setServerError(null);
    const payload = buildPayload();
    if (!payload) {
      setServerError("Selecciona emisor y cliente para previsualizar.");
      return;
    }
    if (!lineas.some((l) => l.descripcion)) {
      setServerError("Añade al menos una línea con descripción.");
      return;
    }
    setPreviewing(true);
    try {
      await previewFacturaPdf(payload);
    } catch (err) {
      if (err instanceof AxiosError) {
        setServerError(
          (err.response?.data as { message?: string })?.message ??
            "No se pudo generar la previsualización.",
        );
      } else {
        setServerError("Error generando la previsualización.");
      }
    } finally {
      setPreviewing(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);

    if (!emisorId || !clienteId) {
      setServerError("Selecciona emisor y cliente.");
      return;
    }
    if (lineas.length === 0) {
      setServerError("Añade al menos una línea.");
      return;
    }

    const formaPago = formasPago?.find((fp) => fp.id === formaPagoId);

    try {
      const factura = await createFactura.mutateAsync({
        emisor_id: emisorId,
        cliente_id: clienteId,
        numero: numero || undefined,
        fecha,
        vencimiento: vencimiento || null,
        numero_pedido: numeroPedido || null,
        numero_oferta: numeroOferta || null,
        forma_pago_id: formaPago?.id ?? null,
        forma_pago_etiqueta: formaPago?.etiqueta ?? null,
        forma_pago_plazo_dias: formaPago?.plazo_dias ?? null,
        forma_pago_iban: formaPago?.iban ?? null,
        forma_pago_notas: formaPago?.notas ?? null,
        lineas: lineas.map((l) => ({
          concepto: l.concepto || null,
          descripcion: l.descripcion,
          cantidad: Number(l.cantidad),
          precio_unitario: Number(l.precio_unitario),
          iva_porcentaje: Number(l.iva_porcentaje),
        })),
        suplidos: suplidos
          .filter((s) => s.descripcion || s.importe)
          .map((s) => ({
            descripcion: s.descripcion || null,
            importe: Number(s.importe),
          })),
      });
      navigate({
        to: "/billing/invoices",
        search: { created: factura.id } as never,
      });
    } catch (err) {
      if (err instanceof AxiosError) {
        const data = err.response?.data as {
          message?: string;
          errors?: Record<string, string[]>;
        };
        const firstError = data?.errors
          ? Object.values(data.errors).flat()[0]
          : null;
        setServerError(
          firstError ?? data?.message ?? "Error al guardar la factura.",
        );
      } else {
        setServerError("Error desconocido.");
      }
    }
  }

  const emisorSeleccionado = emisores?.find((e) => e.id === emisorId);
  const clienteSeleccionado = clientes?.find((c) => c.id === clienteId);
  const formaPagoSeleccionada = formasPago?.find((fp) => fp.id === formaPagoId);

  return (
    <form onSubmit={handleSubmit} className="p-8 max-w-[1400px] mx-auto">
      {/* Hero header */}
      <header className="relative mb-6 overflow-hidden rounded-2xl border border-slate-200/70 bg-gradient-to-br from-white via-emerald-50/40 to-white p-6 shadow-soft">
        <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute right-32 -bottom-16 h-32 w-32 rounded-full bg-brand-700/10 blur-3xl" />

        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link
              to="/billing/invoices"
              className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-brand-700 hover:text-brand-700 shadow-sm"
              title="Volver al listado"
            >
              <ArrowLeft size={18} />
            </Link>
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-brand-700 to-emerald-500 text-white shadow-lg">
              <Receipt size={24} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-black tracking-tight text-slate-900">
                  Nueva factura
                </h1>
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-slate-600">
                  Borrador
                </span>
              </div>
              <p className="mt-0.5 text-sm text-slate-500 flex items-center gap-1.5">
                <Sparkles size={12} className="text-emerald-600" />
                Al guardar se enviará automáticamente a Verifacti si el emisor lo tiene configurado.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePreview}
              disabled={previewing}
              className="inline-flex items-center gap-2 rounded-xl border-2 border-brand-700 bg-white text-brand-700 hover:bg-brand-50 disabled:opacity-50 px-5 py-2.5 font-bold text-sm transition shadow-sm"
            >
              {previewing ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Eye size={18} />
              )}
              Previsualizar PDF
            </button>
            <button
              type="submit"
              disabled={createFactura.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-brand-700 to-emerald-600 hover:from-brand-800 hover:to-emerald-700 disabled:opacity-50 text-white px-5 py-2.5 font-bold text-sm shadow-[0_8px_24px_rgba(11,74,47,0.25)] transition"
            >
              {createFactura.isPending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Save size={18} />
              )}
              Guardar y emitir
            </button>
          </div>
        </div>
      </header>

      {serverError && (
        <div className="rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 p-4 mb-6 flex items-start gap-3 shadow-soft">
          <AlertCircle size={20} className="shrink-0 mt-0.5" />
          <div className="text-sm font-semibold">{serverError}</div>
        </div>
      )}

      {/* Grid principal: form a la izquierda, sticky sidebar de totales a la derecha */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        {/* ── Columna izquierda: formulario ── */}
        <div className="space-y-6 min-w-0">
          {/* Cabecera: emisor + cliente */}
          <section className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden">
            <header className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200 px-6 py-3 flex items-center gap-2">
              <Building2 size={16} className="text-brand-700" />
              <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wide">
                Partes
              </h2>
            </header>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Emisor */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5">
                  <Building2 size={12} /> Emisor <span className="text-rose-500">*</span>
                </label>
                <select
                  required
                  value={emisorId ?? ""}
                  onChange={(e) => {
                    setEmisorId(e.target.value ? Number(e.target.value) : null);
                    setClienteId(null);
                  }}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 outline-none transition"
                >
                  <option value="">— elegir emisor —</option>
                  {emisores?.map((em) => (
                    <option key={em.id} value={em.id}>
                      {em.nombre}
                    </option>
                  ))}
                </select>
                {emisorSeleccionado && (
                  <div className="mt-2 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-1.5">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ background: emisorSeleccionado.color_hex }}
                    />
                    <span className="text-xs font-mono text-slate-700">
                      {emisorSeleccionado.cif ?? "Sin CIF"}
                    </span>
                  </div>
                )}
              </div>

              {/* Cliente */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5">
                  <Users size={12} /> Cliente <span className="text-rose-500">*</span>
                </label>
                <select
                  required
                  disabled={!emisorId}
                  value={clienteId ?? ""}
                  onChange={(e) =>
                    setClienteId(e.target.value ? Number(e.target.value) : null)
                  }
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 outline-none transition disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {emisorId ? "— elegir cliente —" : "Elige primero un emisor"}
                  </option>
                  {clientes?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre}
                      {c.cif ? ` (${c.cif})` : ""}
                    </option>
                  ))}
                </select>
                {clienteSeleccionado && (
                  <div className="mt-2 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-1.5 truncate">
                    <Hash size={11} className="text-slate-400 shrink-0" />
                    <span className="text-xs font-mono text-slate-700 truncate">
                      {clienteSeleccionado.cif ?? "Sin CIF"}
                    </span>
                    {clienteSeleccionado.email && (
                      <span className="text-xs text-slate-500 truncate ml-2">
                        · {clienteSeleccionado.email}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Datos de emisión */}
          <section className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden">
            <header className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200 px-6 py-3 flex items-center gap-2">
              <Calendar size={16} className="text-brand-700" />
              <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wide">
                Datos de emisión
              </h2>
            </header>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5 block">
                  Número{" "}
                  <span className="text-[10px] font-normal normal-case text-slate-400">
                    (autogenerado)
                  </span>
                </label>
                <input
                  type="text"
                  readOnly
                  value={
                    nextNumber ??
                    (emisorId ? "Calculando…" : "Selecciona un emisor")
                  }
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-mono font-bold text-slate-700 cursor-not-allowed select-all"
                  title="Este número se asigna automáticamente al guardar la factura"
                />
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5 block">
                  Fecha <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 outline-none transition"
                />
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5 block">
                  Vencimiento
                </label>
                <input
                  type="date"
                  value={vencimiento}
                  onChange={(e) => setVencimiento(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 outline-none transition"
                />
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5 block">
                  Nº pedido
                </label>
                <input
                  type="text"
                  placeholder="—"
                  value={numeroPedido}
                  onChange={(e) => setNumeroPedido(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 outline-none transition placeholder:text-slate-400 placeholder:font-normal"
                />
              </div>
              <div className="md:col-span-2 lg:col-span-1 lg:col-start-4">
                <label className="text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5 block">
                  Nº oferta
                </label>
                <input
                  type="text"
                  placeholder="—"
                  value={numeroOferta}
                  onChange={(e) => setNumeroOferta(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 outline-none transition placeholder:text-slate-400 placeholder:font-normal"
                />
              </div>
            </div>
          </section>

          {/* Líneas */}
          <section className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden">
            <header className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Receipt size={16} className="text-brand-700" />
                <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wide">
                  Líneas
                </h2>
                <span className="inline-flex items-center justify-center min-w-[24px] h-[20px] rounded-full bg-brand-700 text-white text-[10px] font-black px-1.5">
                  {lineas.length}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setLineas((ls) => [...ls, { ...EMPTY_LINEA, iva_porcentaje: ivaDefecto }])}
                className="inline-flex items-center gap-1.5 rounded-lg border border-brand-700/30 bg-brand-50 hover:bg-brand-100 px-3 py-1.5 text-xs font-bold text-brand-800 transition"
              >
                <Plus size={14} /> Añadir línea
              </button>
            </header>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50/50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  <tr>
                    <th className="text-center px-2 py-2.5 font-black w-10">#</th>
                    <th className="text-left px-3 py-2.5 font-black">Concepto</th>
                    <th className="text-left px-3 py-2.5 font-black">Descripción</th>
                    <th className="text-right px-3 py-2.5 font-black w-20">Cant.</th>
                    <th className="text-right px-3 py-2.5 font-black w-28">Precio</th>
                    <th className="text-right px-3 py-2.5 font-black w-24">IVA %</th>
                    <th className="text-right px-3 py-2.5 font-black w-32">Total</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineas.map((l, idx) => {
                    const base = (l.cantidad || 0) * (l.precio_unitario || 0);
                    const totLin = base + (base * (l.iva_porcentaje || 0)) / 100;
                    return (
                      <tr
                        key={idx}
                        className="border-t border-slate-100 hover:bg-slate-50/50 transition group"
                      >
                        <td className="px-2 py-2 text-center text-xs font-mono font-bold text-slate-400 group-hover:text-brand-700">
                          {idx + 1}
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={l.concepto_id ?? ""}
                            onChange={(e) =>
                              aplicarConcepto(
                                idx,
                                e.target.value ? Number(e.target.value) : null,
                              )
                            }
                            className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs focus:border-brand-600 focus:ring-1 focus:ring-brand-600/30 outline-none transition"
                          >
                            <option value="">— Seleccionar —</option>
                            {conceptos?.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.descripcion}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <textarea
                            required
                            rows={2}
                            placeholder="Descripción de la línea"
                            value={l.descripcion}
                            onChange={(e) =>
                              updateLinea(idx, { descripcion: e.target.value })
                            }
                            className="w-full min-w-[220px] resize-y rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm leading-snug focus:border-brand-600 focus:ring-1 focus:ring-brand-600/30 outline-none transition"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={
                              Number.isFinite(l.cantidad) ? l.cantidad : 1
                            }
                            onChange={(e) => {
                              const n =
                                e.target.value === ""
                                  ? 0
                                  : Number(e.target.value);
                              updateLinea(idx, {
                                cantidad: Number.isFinite(n) ? n : 0,
                              });
                            }}
                            className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-right font-mono focus:border-brand-600 focus:ring-1 focus:ring-brand-600/30 outline-none transition"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="relative">
                            <input
                              type="number"
                              step="0.01"
                              value={
                                Number.isFinite(l.precio_unitario)
                                  ? l.precio_unitario
                                  : 0
                              }
                              onChange={(e) => {
                                const n =
                                  e.target.value === ""
                                    ? 0
                                    : Number(e.target.value);
                                updateLinea(idx, {
                                  precio_unitario: Number.isFinite(n) ? n : 0,
                                });
                              }}
                              className="w-full rounded-md border border-slate-200 bg-white pl-2 pr-6 py-1.5 text-sm text-right font-mono focus:border-brand-600 focus:ring-1 focus:ring-brand-600/30 outline-none transition"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">
                              €
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={l.iva_porcentaje}
                            onChange={(e) =>
                              updateLinea(idx, { iva_porcentaje: Number(e.target.value) })
                            }
                            className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-right font-mono focus:border-brand-600 focus:ring-1 focus:ring-brand-600/30 outline-none transition"
                          >
                            {(ivaTipos.includes(l.iva_porcentaje)
                              ? ivaTipos
                              : [...ivaTipos, l.iva_porcentaje].sort((a, b) => b - a)
                            ).map((t) => (
                              <option key={t} value={t}>
                                {t}%
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className="font-mono font-black text-slate-900 text-sm">
                            {formatEUR(totLin)}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-center">
                          {lineas.length > 1 && (
                            <button
                              type="button"
                              title="Eliminar línea"
                              onClick={() =>
                                setLineas((ls) =>
                                  ls.filter((_, i) => i !== idx),
                                )
                              }
                              className="p-1.5 rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition opacity-0 group-hover:opacity-100"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Suplidos */}
          <section className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden">
            <header className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CircleDollarSign size={16} className="text-amber-600" />
                <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wide">
                  Suplidos
                </h2>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  sin IVA
                </span>
                {suplidos.length > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[24px] h-[20px] rounded-full bg-amber-500 text-white text-[10px] font-black px-1.5">
                    {suplidos.length}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() =>
                  setSuplidos((ss) => [...ss, { descripcion: "", importe: 0 }])
                }
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300/60 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 text-xs font-bold text-amber-800 transition"
              >
                <Plus size={14} /> Añadir suplido
              </button>
            </header>
            {suplidos.length === 0 ? (
              <div className="px-6 py-10 text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 grid place-items-center mb-2">
                  <CircleDollarSign size={20} className="text-slate-400" />
                </div>
                <p className="text-sm text-slate-500 font-semibold">
                  Sin suplidos
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Los suplidos son importes adelantados que no llevan IVA.
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50/50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  <tr>
                    <th className="text-center px-2 py-2.5 font-black w-10">#</th>
                    <th className="text-left px-3 py-2.5 font-black">Descripción</th>
                    <th className="text-right px-3 py-2.5 font-black w-40">Importe</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {suplidos.map((s, idx) => (
                    <tr
                      key={idx}
                      className="border-t border-slate-100 hover:bg-slate-50/50 transition group"
                    >
                      <td className="px-2 py-2 text-center text-xs font-mono font-bold text-slate-400 group-hover:text-amber-600">
                        {idx + 1}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          placeholder="Descripción del suplido"
                          value={s.descripcion}
                          onChange={(e) =>
                            setSuplidos((ss) =>
                              ss.map((x, i) =>
                                i === idx
                                  ? { ...x, descripcion: e.target.value }
                                  : x,
                              ),
                            )
                          }
                          className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 outline-none transition"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="relative">
                          <input
                            type="number"
                            step="0.01"
                            value={s.importe}
                            onChange={(e) =>
                              setSuplidos((ss) =>
                                ss.map((x, i) =>
                                  i === idx
                                    ? { ...x, importe: Number(e.target.value) }
                                    : x,
                                ),
                              )
                            }
                            className="w-full rounded-md border border-slate-200 bg-white pl-2 pr-6 py-1.5 text-sm text-right font-mono focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 outline-none transition"
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">
                            €
                          </span>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <button
                          type="button"
                          title="Eliminar suplido"
                          onClick={() =>
                            setSuplidos((ss) => ss.filter((_, i) => i !== idx))
                          }
                          className="p-1.5 rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition opacity-0 group-hover:opacity-100"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* Forma de pago */}
          <section className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden">
            <header className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200 px-6 py-3 flex items-center gap-2">
              <CreditCard size={16} className="text-brand-700" />
              <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wide">
                Forma de pago
              </h2>
            </header>
            <div className="p-6">
              {!clienteId && (
                <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/50 p-4 text-center">
                  <p className="text-sm text-slate-500">
                    Elige primero un cliente para ver sus formas de pago.
                  </p>
                </div>
              )}
              {clienteId &&
                (formasPago?.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50/50 p-4 text-center">
                    <p className="text-sm font-bold text-amber-800">
                      Este cliente no tiene formas de pago configuradas
                    </p>
                    <p className="text-xs text-amber-600 mt-1">
                      Puedes añadirlas desde el listado de Clientes.
                    </p>
                  </div>
                ) : (
                  <>
                    <select
                      value={formaPagoId ?? ""}
                      onChange={(e) =>
                        setFormaPagoId(
                          e.target.value ? Number(e.target.value) : null,
                        )
                      }
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 outline-none transition"
                    >
                      <option value="">— sin forma de pago —</option>
                      {formasPago?.map((fp) => (
                        <option key={fp.id} value={fp.id}>
                          {fp.etiqueta}
                          {fp.plazo_dias ? ` · ${fp.plazo_dias} días` : ""}
                          {fp.es_predeterminada ? " ★" : ""}
                        </option>
                      ))}
                    </select>
                    {formaPagoSeleccionada && (
                      <div className="mt-3 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-3 text-xs space-y-1">
                        {formaPagoSeleccionada.plazo_dias != null && (
                          <div className="flex justify-between">
                            <span className="text-slate-500 font-semibold">
                              Plazo
                            </span>
                            <span className="font-mono font-bold text-slate-900">
                              {formaPagoSeleccionada.plazo_dias} días
                            </span>
                          </div>
                        )}
                        {formaPagoSeleccionada.iban && (
                          <div className="flex justify-between">
                            <span className="text-slate-500 font-semibold">
                              IBAN
                            </span>
                            <span className="font-mono text-slate-900">
                              {formaPagoSeleccionada.iban}
                            </span>
                          </div>
                        )}
                        {formaPagoSeleccionada.notas && (
                          <div className="text-slate-600 italic pt-1 border-t border-slate-200">
                            {formaPagoSeleccionada.notas}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ))}
            </div>
          </section>
        </div>

        {/* ── Columna derecha: sidebar de totales pegajoso ── */}
        <aside className="lg:sticky lg:top-6 self-start space-y-4">
          <section className="relative overflow-hidden rounded-2xl border border-brand-700/20 bg-gradient-to-br from-slate-900 to-emerald-900 text-white shadow-2xl">
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-emerald-400/20 blur-3xl" />
            <div className="absolute -left-10 -bottom-10 h-32 w-32 rounded-full bg-emerald-500/20 blur-3xl" />

            <div className="relative p-6">
              <div className="flex items-center gap-2 mb-1">
                <CircleDollarSign size={16} className="text-emerald-300" />
                <span className="text-[11px] font-black uppercase tracking-[0.2em] text-emerald-300">
                  Total factura
                </span>
              </div>
              <div className="text-4xl font-black tracking-tight">
                {formatEUR(totales.total)}
              </div>
              <div className="text-xs text-emerald-200/80 mt-1">
                {lineas.length} línea{lineas.length !== 1 ? "s" : ""}
                {suplidos.length > 0 &&
                  ` + ${suplidos.length} suplido${suplidos.length !== 1 ? "s" : ""}`}
              </div>

              <div className="mt-6 space-y-2 text-sm border-t border-white/15 pt-4">
                <div className="flex justify-between">
                  <span className="text-white/70 font-semibold">Subtotal</span>
                  <span className="font-mono font-bold">
                    {formatEUR(totales.subtotal)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70 font-semibold">IVA</span>
                  <span className="font-mono font-bold">
                    {formatEUR(totales.iva)}
                  </span>
                </div>
                {totales.suplidos_total > 0 && (
                  <div className="flex justify-between">
                    <span className="text-white/70 font-semibold">
                      Suplidos
                    </span>
                    <span className="font-mono font-bold">
                      {formatEUR(totales.suplidos_total)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Resumen del estado del form */}
          <section className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
            <h3 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-3">
              Resumen
            </h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <span
                  className={`w-2 h-2 rounded-full ${
                    emisorId ? "bg-emerald-500" : "bg-slate-300"
                  }`}
                />
                <span
                  className={
                    emisorId ? "text-slate-900 font-bold" : "text-slate-500"
                  }
                >
                  Emisor seleccionado
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span
                  className={`w-2 h-2 rounded-full ${
                    clienteId ? "bg-emerald-500" : "bg-slate-300"
                  }`}
                />
                <span
                  className={
                    clienteId ? "text-slate-900 font-bold" : "text-slate-500"
                  }
                >
                  Cliente seleccionado
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span
                  className={`w-2 h-2 rounded-full ${
                    lineas.some((l) => l.descripcion && l.precio_unitario > 0)
                      ? "bg-emerald-500"
                      : "bg-slate-300"
                  }`}
                />
                <span
                  className={
                    lineas.some((l) => l.descripcion && l.precio_unitario > 0)
                      ? "text-slate-900 font-bold"
                      : "text-slate-500"
                  }
                >
                  Al menos una línea con datos
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span
                  className={`w-2 h-2 rounded-full ${
                    formaPagoId ? "bg-emerald-500" : "bg-slate-300"
                  }`}
                />
                <span
                  className={
                    formaPagoId ? "text-slate-900 font-bold" : "text-slate-500"
                  }
                >
                  Forma de pago (opcional)
                </span>
              </li>
            </ul>
          </section>
        </aside>
      </div>
    </form>
  );
}
